import styles from './style.module.scss';
import Collapsible from 'react-collapsible';
import { WebPNextImage as Image } from '@/components/WebPImage';
import { useReducer, useRef } from 'react';
import type { IFeatureWithDescAndPic } from '@/utils/const/products/interface';
import classnames from 'classnames';

interface CollapseListProps {
  list: IFeatureWithDescAndPic[];
  /** 当前展开的索引 */
  active: number;
  /** 设置索引的方法 */
  setActive: React.Dispatch<React.SetStateAction<number>>;
  /** 是否允许折叠所有面板 */
  couldCloseAll?: boolean;
}

/**
 * 折叠面板（此组件PC独占）
 * 具体效果见：快手云剪-最佳实践、语聊/K歌-使用场景
 */
const CollapseListWithImages = ({
  list,
  active,
  setActive,
  couldCloseAll = false,
  reverse = false,
}: CollapseListProps & {
  reverse?: boolean;
}) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const loaded = useRef<boolean>(false);
  const onImageLoad = () => {
    loaded.current = true;
    forceUpdate();
  };
  return (
    <section
      className={styles['collapse-content-wrapper']}
      style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}
    >
      <section
        style={{
          height: (list.length - 1) * 67 + 383 + 'px',
        }}
        className={styles['collapse-left-content']}
      >
        <div className={styles['collapse-list-container']}>
          <CollapseList
            couldCloseAll={couldCloseAll}
            loaded={loaded}
            active={active}
            setActive={setActive}
            list={list}
          />
        </div>
      </section>
      <section className={styles['collapse-right-content']}>
        <div
          style={
            loaded.current
              ? { opacity: 1, transition: 'opacity 0.6s ease' }
              : { opacity: 0 }
          }
          className={styles['collapse-right-img-container']}
        >
          <Image
            onLoad={onImageLoad}
            alt="配图"
            src={list[active]?.picUrl || list[0]?.picUrl}
            draggable={false}
            layout="fill"
            objectFit="cover"
          />
        </div>
      </section>
    </section>
  );
};

export const CollapseList = ({
  list,
  loaded,
  setActive,
  active,
  couldCloseAll = false,
}: CollapseListProps & {
  loaded: React.MutableRefObject<boolean>;
}) => {
  return (
    <>
      {list.map((item, index) => {
        return (
          <div
            key={index}
            onMouseEnter={() => {
              loaded.current = false;
              setActive(index);
            }}
          >
            <Collapsible
              onTriggerOpening={() => {
                loaded.current = false;
                setActive(index);
              }}
              onTriggerClosing={() => {
                loaded.current = false;
                setActive(-1);
              }}
              open={index === active}
              transitionTime={300}
              // NOTE: 如果不允许关闭全部, 那么当目前是展开态的时候,就不能关闭
              triggerDisabled={!couldCloseAll && index === active}
              trigger={
                <div
                  className={classnames({
                    [styles['collapse-trigger-container']]: true,
                    [styles['collapse-trigger-container-active']]:
                      index === active,
                  })}
                >
                  {item.title}
                </div>
              }
            >
              <div className={styles['collapse-content-container']}>
                <div className={styles['collapse-content']}>
                  {item.subTitle}
                </div>
                <div className={styles['collapse-label']}>
                  <Image
                    draggable={false}
                    src="/img/useCaseLabel.webp"
                    alt="useCaseLabel"
                    layout={'fill'}
                  />
                </div>
              </div>
            </Collapsible>
            <div className={styles.divider} />
          </div>
        );
      })}
    </>
  );
};

export default CollapseListWithImages;
